import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

// Import the components we just created/fixed
import { RadioCard } from "./RadioCard";
import Button from "../../../../../../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../../../components/button/types";
import { DragAndDropUploaderForCv } from "./DragAndDrop";

type StartOption = "upload" | "no-cv";

interface InitialStepProps {
  // Mutations passed from the parent modal
  onUpload: (cvFile: File) => void;
  onProceedWithoutCv: () => void;
  onProceedWithCv: () => void;

  // State passed from the parent modal
  isLoading: boolean;
  cvFileName?: string | null;
}

export const InitialStep: React.FC<InitialStepProps> = ({
  onUpload,
  onProceedWithoutCv,
  onProceedWithCv,
  isLoading,
  cvFileName,
}) => {
  const { t } = useTranslation("cvEditor");

  // This state now only controls which radio button is visually selected
  const [selectedOption, setSelectedOption] = useState<StartOption>("upload");
  const [fileError, setFileError] = useState<string | null>(null);

  const handleFileUpdate = (files: File[]) => {
    const file = files[0];
    if (!file) return;

    // Basic file validation
    if (file.size > 2 * 1024 * 1024) {
      // 2MB limit
      setFileError(t("upload.fileSizeError"));
      return;
    }
    if (file.type !== "application/pdf") {
      setFileError(t("upload.fileTypeError"));
      return;
    }

    setFileError(null);
    // Immediately trigger the upload mutation when a valid file is selected
    onUpload(file);
  };

  const handleNext = () => {
    if (selectedOption === "no-cv") {
      onProceedWithoutCv();
    } else if (selectedOption === "upload" && cvFileName) {
      onProceedWithCv();
    }
  };

  // The "Next" button is enabled only when a valid path has been chosen.
  const isNextDisabled =
    isLoading || !(cvFileName || selectedOption === "no-cv");

  return (
    <div className="p-6 flex flex-col h-full">
      <div className="flex-grow overflow-y-auto pr-2 -mr-2">
        <h3 className="text-lg font-medium text-gray-900 mb-1">
          {t("initialStep.title")}
        </h3>
        <p className="text-gray-600 mb-4">{t("initialStep.description")}</p>

        <RadioCard
          value="upload"
          label={t("initialStep.upload.title")}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          disabled={isLoading}
          icon={<CloudUploadIcon className="relative top-[-4px]" />}
        >
          <div className="flex justify-center w-full ">
            <DragAndDropUploaderForCv
              onFileUpdate={handleFileUpdate}
              uploadedFileName={cvFileName}
              disabled={isLoading}
            />
          </div>
          {fileError && (
            <p className="text-xs text-red-600 mt-1 text-center">{fileError}</p>
          )}
        </RadioCard>

        <RadioCard
          value="no-cv"
          label={t("initialStep.scratch.title")}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          disabled={isLoading}
          icon={<AddCircleOutlineIcon className="relative top-[-3px]" />}
        >
          <p className="text-sm text-gray-500">
            {t("initialStep.scratch.description")}
          </p>
        </RadioCard>
      </div>

      <div className="flex-shrink-0 mt-auto pt-4 border-t border-gray-200">
        <div className="flex justify-end ">
          <Button
            type="button"
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SMALL}
            onClick={handleNext}
            disabled={isNextDisabled}
            isLoading={isLoading}
            className="w-full"
          >
            {t("common.start")}
          </Button>
        </div>
      </div>
    </div>
  );
};
